import {
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { User } from 'api/types';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useOPUserQuery from 'hooks/useOPUserQuery';
import useUsersQuery from 'hooks/useUsersQuery';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useBlockUserMutation } from './useBlockUserMutation';
import { useUnblockUserMutation } from './useUnblockUserMutation';

type Props = {
  user: User;
};

export function ManageUserAccess({ user }: Props) {
  const { t } = useTranslation('user');
  const queryClient = useQueryClient();
  const toast = useToast();

  const usersQuery = useUsersQuery();
  const activeUsers = useMemo(() => (usersQuery.data ?? []).filter((user) => !user.blocked), [usersQuery.data]);
  const { mutate: blockUser } = useBlockUserMutation(user.id);
  const { mutate: unblockUser } = useUnblockUserMutation(user.id);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const reassignSchema = useMemo(
    () =>
      yup.object().shape({
        taskReassignUserId: yup.number().required(t('danger_zone.suspend_user.dialog.assign_to.required')),
      }),
    [t],
  );

  const { handleSubmit, reset, control } = useForm({
    resolver: yupResolver(reassignSchema),
  });

  function closeAndReset() {
    reset();
    onClose();
  }

  function onSubmit({ taskReassignUserId }: yup.InferType<typeof reassignSchema>) {
    blockUser(
      { task_reassign_user_id: taskReassignUserId },
      {
        onSuccess: () => {
          onClose();
          queryClient.invalidateQueries({ queryKey: useOPUserQuery.getKey(user.id) });
          toast({
            title: t('danger_zone.suspend_user.success', { username: user.name }),
            status: 'success',
            ...defaultToastOptions,
          });
        },
        onError: (error) => {
          toast({
            title: error.response?.data?.message ?? error.message,
            status: 'error',
            ...defaultToastOptions,
          });
        },
      },
    );
  }

  function onUnblock() {
    unblockUser(undefined, {
      onSuccess: () => {
        onClose();
        queryClient.invalidateQueries({ queryKey: useOPUserQuery.getKey(user.id) });
        toast({
          title: t('danger_zone.reactivate_user.success', { username: user.name }),
          status: 'success',
          ...defaultToastOptions,
        });
      },
      onError: (error) => {
        toast({
          title: error.response?.data?.message ?? error.message,
          status: 'error',
          ...defaultToastOptions,
        });
      },
    });
  }

  return (
    <>
      <HStack gap={4} justifyContent="space-between">
        {!user.blocked ? (
          <>
            <Text size="lg">{t('danger_zone.suspend_user.label')}</Text>
            <Button colorScheme="red" onClick={onOpen}>
              {t('danger_zone.suspend_user.button.label')}
            </Button>
          </>
        ) : (
          <>
            <Text size="lg">{t('danger_zone.reactivate_user.label')}</Text>
            <Button colorScheme="brand" onClick={onOpen}>
              {t('danger_zone.reactivate_user.button.label')}
            </Button>
          </>
        )}
      </HStack>

      {!user.blocked ? (
        <Modal isOpen={isOpen} onClose={closeAndReset} size="xl">
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('danger_zone.suspend_user.dialog.heading', { username: user.name })}</ModalHeader>
              <ModalBody>
                {
                  <>
                    <Text>{t('danger_zone.suspend_user.dialog.has_open_tasks')}</Text>
                    <Controller
                      name="taskReassignUserId"
                      render={({ field, formState: { errors } }) => (
                        <FormControl isInvalid={errors.taskReassignUserId != null} marginBlock={4}>
                          <Select placeholder={t('danger_zone.suspend_user.dialog.assign_to.placeholder')} {...field}>
                            {activeUsers.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.taskReassignUserId?.message}</FormErrorMessage>
                        </FormControl>
                      )}
                      control={control}
                    />
                  </>
                }
              </ModalBody>
              <ModalFooter gap={4}>
                <Button onClick={closeAndReset}>{t('danger_zone.suspend_user.dialog.cancel_button.label')}</Button>
                <Button colorScheme="red" type="submit">
                  {t('danger_zone.suspend_user.dialog.confirm_button.label')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </form>
        </Modal>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('danger_zone.reactivate_user.dialog.heading', { username: user.name })}</ModalHeader>
            <ModalFooter gap={4}>
              <Button onClick={onClose}>{t('danger_zone.reactivate_user.dialog.cancel_button.label')}</Button>
              <Button colorScheme="brand" onClick={onUnblock}>
                {t('danger_zone.reactivate_user.dialog.confirm_button.label')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
